// ---------------------------------------------------------------------------
// PWA_ControlBillet — Statistics dashboard screen
// Shows scan stats, success rate, sync status, and key status.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { useScanStore } from '@/store/scan-store';
import { useAppStore } from '@/store/app-store';
import { StatCard } from '@/components/StatCard';
import type { KeyStatus } from '@/lib/types';

// ---------------------------------------------------------------------------
// Stat icons (inline SVG)
// ---------------------------------------------------------------------------

function TotalIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-accent-light"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ValidIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-success"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-danger"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-duplicate"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StatsScreen() {
  const stats = useScanStore((s) => s.todayStats);
  const connectionStatus = useAppStore((s) => s.connectionStatus);
  const pendingCount = useAppStore((s) => s.pendingCount);

  const [syncing, setSyncing] = useState(false);
  const [keyStatus, setKeyStatus] = useState<KeyStatus | null>(null);

  const isOnline = connectionStatus === 'online';
  const successRate =
    stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0;

  // Load key status on mount.
  useEffect(() => {
    async function loadKeyStatus() {
      try {
        const { keyManager } = await import('@/crypto/key-manager');
        const status = keyManager.getStatus();
        setKeyStatus(status);
      } catch {
        // key-manager service not yet created.
      }
    }
    loadKeyStatus();
  }, []);

  // Sync handler.
  async function handleSync() {
    setSyncing(true);
    try {
      const { syncManager } = await import('@/services/sync-manager');
      await syncManager.syncNow();
    } catch {
      // sync-manager service not yet created.
    }
    setSyncing(false);
  }

  // Format date for key rotation display.
  function formatDate(dateStr: string | null): string {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <div className="h-full overflow-y-auto pt-14 pb-20 px-4 space-y-5">
      {/* Title */}
      <div className="pt-4">
        <h2 className="text-lg font-bold text-white">
          Statistiques du jour
        </h2>
        <p className="text-gray-500 text-xs">
          {new Date().toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
      </div>

      {/* 2x2 stat grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Total scans"
          value={stats.total}
          color="text-accent-light"
          icon={<TotalIcon />}
        />
        <StatCard
          label="Validations"
          value={stats.valid}
          color="text-success"
          icon={<ValidIcon />}
        />
        <StatCard
          label="Echecs"
          value={stats.invalid}
          color="text-danger"
          icon={<ErrorIcon />}
        />
        <StatCard
          label="Doublons"
          value={stats.duplicate}
          color="text-duplicate"
          icon={<DuplicateIcon />}
        />
      </div>

      {/* Success rate bar */}
      <div className="bg-surface-card rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400 font-medium">
            Taux de reussite
          </span>
          <span className="text-lg font-bold text-white">{successRate}%</span>
        </div>
        <div className="w-full h-3 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-success rounded-full transition-all duration-500 ease-out"
            style={{ width: `${successRate}%` }}
          />
        </div>
        {stats.total === 0 && (
          <p className="text-gray-600 text-xs mt-2 text-center">
            Aucun scan effectue
          </p>
        )}
      </div>

      {/* Sync status section */}
      <div className="bg-surface-card rounded-2xl p-4 space-y-3">
        <h3 className="text-sm font-semibold text-white">Synchronisation</h3>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Connexion</span>
          <span
            className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
              isOnline
                ? 'bg-success/20 text-success'
                : 'bg-danger/20 text-danger'
            }`}
          >
            {isOnline ? 'En ligne' : 'Hors ligne'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">
            Validations en attente
          </span>
          <span
            className={`text-sm font-bold ${
              pendingCount > 0 ? 'text-warning' : 'text-gray-500'
            }`}
          >
            {pendingCount}
          </span>
        </div>

        <button
          type="button"
          onClick={handleSync}
          disabled={syncing || !isOnline || pendingCount === 0}
          className="w-full btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {syncing ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Synchronisation...
            </span>
          ) : (
            'Sync maintenant'
          )}
        </button>
      </div>

      {/* Key status section */}
      <div className="bg-surface-card rounded-2xl p-4 space-y-3">
        <h3 className="text-sm font-semibold text-white">
          Cle de chiffrement
        </h3>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Version</span>
          <span className="text-sm text-white font-mono">
            {keyStatus?.keyVersion ?? 'N/A'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Derniere rotation</span>
          <span className="text-xs text-gray-300">
            {formatDate(keyStatus?.lastRotation ?? null)}
          </span>
        </div>

        {keyStatus?.needsRotation && (
          <div className="flex items-center gap-2 bg-warning/10 rounded-lg px-3 py-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-warning shrink-0"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span className="text-warning text-xs font-medium">
              Rotation de cle necessaire
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
